/* eslint-disable max-len */
import { useRef, useState } from "react";
import {
  Edit,
  SimpleForm,
  TextInput,
  SelectArrayInput,
  useTranslate,
  NumberInput,
  ReferenceInput,
  useEditContext,
  Translate,
  BooleanInput,
  Labeled,
  AutocompleteInput
} from "react-admin";
import { useNotify } from "react-admin";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useQuery } from "@apollo/client";
import {
  CardElement,
  Elements,
  useElements,
  useStripe
} from "@stripe/react-stripe-js";
import { stripePromise } from "stripeConfig";

import { atUsernameRegex, facebookRegex } from "utils/validation";
import * as User from "providers/user";
import * as Axios from "providers/axios";
import { usePhotoUploadSubmitEdit } from "hooks/usePhotoUploadSubmitEdit";
import { timeValues } from "utils/timeValues";
import CustomHeaderActions from "./CustomHeaderActions";
import ShowAndEditDetails from "./ShowAndEditDetails";
import {
  CancelledIcon,
  CompanyDetailsIcon,
  ShowButtonIcon
} from "assets/Icons";
import CustomImageInputSmall from "./CustomImageInputSmall";
import CustomImageInputWrapper from "./CustomImageInputWrapper";
import CustomImageInput from "./CustomImageInput";
import { useParams } from "react-router-dom";
import AdvancedDetails from "./AdvancedDetails";
import PercentageDecimalNumberInput from "./PercentageDecimalNumberInput";
import { useFormContext } from "react-hook-form";
import {
  COMPANY_STRIPE_REFRESH_PATH,
  COMPANY_STRIPE_AUTH_PATH
} from "utils/constants";
import { CustomToolbar } from "./CustomToolbar";
import { amenitiesChoices } from "utils/helpers";
import { CustomButton } from "./CustomButton";
import { Link } from "react-router-dom";
import { CardComponent } from "./UserEdit";
import ShowCards from "./ShowCards";
import {
  GET_COMPANY_PAYMENT_METHOD,
  GET_NEXT_FUTURE_APPOINTMENT_SERVICE_ID
} from "../gql";
import ChoosePlanModalContainer from "./choosePlanModal/ChoosePlanModal";
import { Box } from "@mui/material";
import GooglePlacesAutocomplete from "./GooglePlacesAutocomplete";
import ConfirmationModal from "./ConfirmationModal";
import { muiColors } from "themes/colors";
import FreeTrialModal from "./FreeTrialModal";
import dayjs from "dayjs";

const paymentTypes = [
  { id: "PAY_FULL_UPFRONT", name: "ra.choices.PAY_FULL_UPFRONT" },
  { id: "PAY_IN_STORE", name: "ra.choices.PAY_IN_STORE" }
];

const AdvancedDetailsContainer = ({ translate }: { translate: Translate }) => {
  const { permissions } = User.useUser();
  const form = useFormContext();
  const errorsArray = Object.keys(form?.formState?.errors);
  const isErrorInAdvancedDetails = errorsArray.find((item) =>
    [
      "default_service_id",
      "timeslot_spacing",
      "late_cancellation_hours",
      "late_cancellation_charge",
      "no_show_charge",
      "retail_tax_rate"
    ].includes(item)
  );

  return (
    <AdvancedDetails open={!!isErrorInAdvancedDetails}>
      <div>
        <ReferenceInput
          source="default_service_id"
          reference="Service"
          label={translate("companies.default_service")}
          fullWidth
          filter={{ company_id: permissions?.companyId, is_addon: false }}
        >
          <AutocompleteInput
            optionText="name"
            label={translate("companies.default_service")}
            fullWidth
          />
        </ReferenceInput>
        {!permissions?.isCompanyLimited && !permissions?.isCompanyBasic && (
          <BooleanInput
            source="user_pays_fees"
            label={translate("companies.user_pays_fees")}
            fullWidth
          />
        )}
        {!permissions?.isCompanyLimited && !permissions?.isCompanyBasic && (
          <AutocompleteInput
            source="timeslot_spacing"
            label={translate("companies.timeslot_spacing")}
            choices={timeValues}
            fullWidth
          />
        )}
        {!permissions?.isCompanyLimited && !permissions?.isCompanyBasic && (
          <NumberInput
            source="late_cancellation_hours"
            label={translate("companies.late_cancellation_hours")}
            fullWidth
          />
        )}
        {!permissions?.isCompanyLimited && !permissions?.isCompanyBasic && (
          <PercentageDecimalNumberInput
            max={100}
            min={0}
            step={5}
            source="late_cancellation_charge"
            label={translate("companies.late_cancellation_charge")}
          />
        )}
        {!permissions?.isCompanyLimited && !permissions?.isCompanyBasic && (
          <PercentageDecimalNumberInput
            max={100}
            min={0}
            step={5}
            source="no_show_charge"
            label={translate("companies.no_show_charge")}
          />
        )}
        {!permissions?.isCompanyLimited && !permissions?.isCompanyBasic && (
          <PercentageDecimalNumberInput
            source="retail_tax_rate"
            label={translate("companies.retail_tax_rate")}
            min={0}
            max={10}
            defaultValue={0}
            step={0.5}
          />
        )}
      </div>
    </AdvancedDetails>
  );
};

const ConnectStripeButton = () => {
  const { record } = useEditContext();
  const translate = useTranslate();

  const connectStripe = async () => {
    // This API route will return an onboarding URL new & existing (but not fully onboarded)
    // stripe accounts.
    const response = await fetch(
      `${process.env.REACT_APP_API_CORE_URL}${COMPANY_STRIPE_AUTH_PATH}?companyId=${record.id}`
    );
    const { url } = await response.json();
    window.location.href = url;
  };

  const refreshStripe = async () => {
    await fetch(
      `${process.env.REACT_APP_API_CORE_URL}${COMPANY_STRIPE_REFRESH_PATH}?companyId=${record.id}`
    );
    window.location.reload();
  };

  const companyHasStripeAcct: boolean = record.stripe_user_id !== null;
  const stripeIsFullyOnboarded: boolean =
    record.stripe_charges_enabled && record.stripe_details_submitted;
  const stripeIsNotFullyOnboarded: boolean = !stripeIsFullyOnboarded;

  return (
    <>
      {stripeIsFullyOnboarded && (
        <div className="text-[14px]">
          {translate("companies.stripe_account_connected_charges_enabled")}
        </div>
      )}
      {companyHasStripeAcct && stripeIsNotFullyOnboarded && (
        <CustomButton
          variant="secondary"
          text={translate("companies.finish_onboarding_stripe")}
          guideId="connect-stripe-button"
          onClick={connectStripe}
          sx={{
            "@media screen and (max-width:640px)": {
              width: "100%"
            }
          }}
        />
      )}
      {!record.stripe_user_id && (
        <CustomButton
          variant="secondary"
          text={translate("companies.connect_stripe")}
          guideId="connect-stripe-button"
          onClick={connectStripe}
          sx={{
            "@media screen and (max-width:640px)": {
              width: "100%"
            }
          }}
        />
      )}
      {stripeIsFullyOnboarded && (
        <CustomButton
          variant="secondary"
          text={translate("companies.refresh")}
          onClick={refreshStripe}
          sx={{
            "@media screen and (max-width:640px)": {
              width: "100%"
            }
          }}
        />
      )}
    </>
  );
};

const cancelWarningMessages = {
  "BASIC-WITHOUT": "companies.basic_cancel_without_appointments",
  "LIMITED-WITH": "companies.limited_cancel_with_appointments",
  "LIMITED-WITHOUT": "companies.limited_cancel_without_appointments",
  "FULL-WITH": "companies.full_cancel_with_appointments",
  "FULL-WITHOUT": "companies.full_cancel_without_appointments"
};

const ChangePlan = ({
  handleCancelPlan,
  handleRenewPlan,
  isLoading
}: {
  handleCancelPlan: () => void;
  handleRenewPlan: () => void;
  isLoading: boolean;
}) => {
  const { record } = useEditContext();
  const { permissions, user } = User.useUser();
  const translate = useTranslate();
  const [isPlanModalOpen, setIsPlanModalOpen] = useState<boolean>(false);
  const [confirmRequired, setConfirmRequired] = useState<
    "CHANGE" | "RENEW" | null
  >(null);
  const [cancelPlanConfirm, setCancelPlanConfirm] = useState<boolean>(false);

  const locationIds = permissions.isArtist
    ? user?.joined_company?.locations?.map((l: any) => l.id)
    : permissions.impersonateLocationIds.length > 0
    ? permissions.impersonateLocationIds
    : user?.company?.locations?.map((l: any) => l.id);

  const nowIsoRef = useRef<string>(dayjs().toISOString());

  const { data } = useQuery(GET_NEXT_FUTURE_APPOINTMENT_SERVICE_ID, {
    variables: {
      where: {
        AND: [
          { start_time: { gt: nowIsoRef.current } },
          {
            appointment: {
              is: {
                cancelled_at: { equals: null },
                location_id: { in: locationIds }
              }
            }
          }
        ]
      },
      take: 1,
      orderBy: [{ start_time: "asc" }]
    },
    fetchPolicy: "no-cache"
  });

  const hasAnyFutureBooking = (data?.appointmentServices?.length ?? 0) > 0;

  return (
    <div className="w-full mb-4">
      <div className="w-full flex-wrap flex items-center justify-between gap-4">
        <div className="flex items-center w-full sm:w-auto justify-between gap-5">
          <div className="text-[16px] leading-10 text-[rgba(48,47,46,0.95)] font-[400] text-left">
            {translate("companies.selected_plan")}
            {": "}
            <span
              className="text-[rgba(184,114,42,1)] text-[28px] font-[600] font-['Famous']"
              style={{ letterSpacing: "4px" }}
            >
              {record?.status}
            </span>
          </div>
          <CustomButton
            variant="secondary"
            text={translate("companies.change_a_plan")}
            onClick={() => {
              if (record?.StripePaymentMethods?.[0]?.id && record?.address) {
                setIsPlanModalOpen(true);
              } else {
                setConfirmRequired("CHANGE");
              }
            }}
          />
        </div>
        <div className="flex items-center w-full sm:w-auto justify-between gap-2">
          {record?.current_period_end_at && (
            <div className="text-[14px] text-[rgba(48,47,46,0.8)] mr-2">
              {translate("companies.next_payment_on")}{" "}
              {new Date(record.current_period_end_at).toLocaleDateString()}
            </div>
          )}
          {record?.cancel_at_period_end_at && (
            <div className="text-[14px] text-[rgba(48,47,46,0.8)] mr-2">
              {translate("companies.active_until")}{" "}
              {new Date(record.cancel_at_period_end_at).toLocaleDateString()}
            </div>
          )}
          {!record?.cancel_at_period_end_at &&
            (record?.status === "LIMITED" ||
              record?.stripe_subscription_id) && (
              <CustomButton
                onClick={() => {
                  setCancelPlanConfirm(true);
                }}
                text={translate("companies.cancel_plan")}
                variant="secondary"
                disabled={isLoading}
                sx={{
                  border: `dashed 1px ${muiColors.button.disabled}`,
                  color: muiColors.error,
                  svg: {
                    fill: muiColors.error,
                    fillOpacity: 1,
                    path: {
                      fill: muiColors.error,
                      fillOpacity: 1
                    }
                  }
                }}
                startIcon={<CancelledIcon />}
              />
            )}
          {record?.cancel_at_period_end_at && (
            <span>
              <CustomButton
                onClick={() => {
                  if (record?.StripePaymentMethods?.[0]?.id) {
                    handleRenewPlan();
                  } else {
                    setConfirmRequired("RENEW");
                  }
                }}
                text={translate("companies.renew_plan")}
                variant="primary"
                disabled={isLoading}
              />
            </span>
          )}
        </div>
      </div>
      {isPlanModalOpen && (
        <ChoosePlanModalContainer
          open={isPlanModalOpen}
          alreadySelectedPlan={record.status}
          hasSubscription={!!record?.stripe_subscription_id}
          onClose={() => setIsPlanModalOpen(false)}
          currentPeriodEndAt={record?.current_period_end_at}
          cancelAtPeriodEndAt={record?.cancel_at_period_end_at}
          hasAnyFutureBooking={hasAnyFutureBooking}
        />
      )}
      {confirmRequired && (
        <ConfirmationModal
          open={!!confirmRequired}
          handleConfirm={() => setConfirmRequired(null)}
          onClose={() => setConfirmRequired(null)}
          text={(() => {
            const hasPaymentMethod = Boolean(
              record?.StripePaymentMethods?.[0]?.id
            );
            const hasBillingAddress = Boolean(record?.address);
            if (confirmRequired === "RENEW") {
              if (!hasPaymentMethod && !hasBillingAddress) {
                return translate(
                  "companies.please_enter_payment_method_and_billing_address_to_renew"
                );
              }
              if (hasPaymentMethod && !hasBillingAddress) {
                return translate(
                  "companies.please_enter_your_billing_address_to_renew"
                );
              }
              return translate(
                "companies.please_enter_your_preferred_payment_method_to_renew"
              );
            }
            if (record?.status === "BASIC") {
              return translate("companies.to_upgrade_to_a_paid_plan_please");
            }
            if (!hasPaymentMethod && !hasBillingAddress) {
              return translate(
                "companies.a_payment_method_and_billing_address_are_required_to_change_this_plan"
              );
            }
            if (hasPaymentMethod && !hasBillingAddress) {
              return translate(
                "companies.a_billing_address_is_required_to_change_this_plan"
              );
            }
            return translate(
              "companies.a_payment_method_is_required_to_change_this_plan"
            );
          })()}
          buttonText={translate("appointment.got_it")}
          title=""
          buttonBg={muiColors.button.primary}
          centerAlign
        />
      )}
      {cancelPlanConfirm && (
        <ConfirmationModal
          open={Boolean(cancelPlanConfirm)}
          handleConfirm={() => {
            handleCancelPlan();
            setCancelPlanConfirm(false);
          }}
          onClose={() => setCancelPlanConfirm(false)}
          text={translate(
            cancelWarningMessages[
              `${record?.status}-${
                hasAnyFutureBooking ? "WITH" : "WITHOUT"
              }` as keyof typeof cancelWarningMessages
            ]
          )?.replaceAll(
            "[DATE]",
            new Date(
              record?.current_period_end_at || record?.cancel_at_period_end_at
            ).toLocaleDateString()
          )}
          buttonText={translate("companies.yes_cancel")}
          title=""
          buttonBg={muiColors.error}
          centerAlign
        />
      )}
    </div>
  );
};

export default function CompanyEditContainer() {
  return (
    <Elements stripe={stripePromise}>
      <CompanyEdit />
    </Elements>
  );
}

function FormErrorCapture({
  setErrorRef
}: {
  setErrorRef: React.MutableRefObject<
    ReturnType<typeof useFormContext>["setError"] | null
  >;
}) {
  const { setError } = useFormContext();
  setErrorRef.current = setError;
  return null;
}

function CompanyEdit() {
  const { permissions } = User.useUser();
  const { api } = Axios.useAxios();
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isNewCardEmpty, setIsNewCardEmpty] = useState<boolean>(true);
  const [confirmFreeTrial, setConfirmFreeTrial] = useState<boolean>(false);
  const [isFreeTrialDays, setIsFreeTrialDays] = useState<number>(30);
  const [selectedDefaultCardIdState, setSelectedDefaultCardIdState] =
    useState<string>("");
  const [stripeErrorMsg, setStripeErrorMsg] = useState<string>("");
  const formSetErrorRef = useRef<
    ReturnType<typeof useFormContext>["setError"] | null
  >(null);
  const stripe = useStripe();
  const elements = useElements();
  const { submit: submitWithPhoto } = usePhotoUploadSubmitEdit({
    redirect:
      id === permissions.companyId
        ? `/dashboard/Company/${id}/show`
        : `/dashboard/Company`,
    resource: "Company",
    keys: ["cover_photo_url", "logo_url"],
    setIsLoading: setIsLoading
  });
  const translate = useTranslate();
  const notify = useNotify();

  const { data: companyData, refetch } = useQuery(GET_COMPANY_PAYMENT_METHOD, {
    variables: {
      where: { id }
    }
  });

  const defaultCard =
    companyData?.company?.StripePaymentMethods?.find(
      (card: any) => card.is_default
    ) || null;

  const submit = async (data: any) => {
    setIsLoading(true);
    if (data.fakeDirtyFormWorkaround) {
      delete data.fakeDirtyFormWorkaround;
    }
    if (
      permissions?.isCompanyLimited &&
      !permissions?.isSuperAdmin &&
      isNewCardEmpty &&
      companyData?.company?.StripePaymentMethods?.length < 1
    ) {
      const message = "Payment method is required.";
      notify(message, { type: "error" });
      setStripeErrorMsg(message);
      setIsLoading(false);
      return;
    }
    if (stripe && elements && !isNewCardEmpty) {
      if (
        !data.address ||
        !data.city ||
        !data.state ||
        !data.zipcode ||
        !data.country
      ) {
        const message =
          "To add a new payment method, billing address is required.";
        notify(message, { type: "error" });
        setStripeErrorMsg(message);
        const setError = formSetErrorRef.current;
        if (setError) {
          if (!data.address)
            setError("address", {
              type: "manual",
              message: "Address is required"
            });
          if (!data.city)
            setError("city", { type: "manual", message: "City is required" });
          if (!data.state)
            setError("state", { type: "manual", message: "State is required" });
          if (!data.zipcode)
            setError("zipcode", {
              type: "manual",
              message: "Zipcode is required"
            });
          if (!data.country)
            setError("country", {
              type: "manual",
              message: "Country is required"
            });
        }
        setIsLoading(false);
        return;
      }
      const cardElement = elements.getElement(CardElement);

      if (cardElement) {
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: {
            name: companyData?.company.name,
            email: companyData?.company.owner.email,
            phone: companyData?.company.owner.phone_number || ""
          }
        });

        if (error) {
          const message = error?.message || "";
          notify(message, { type: "error" });
          setStripeErrorMsg(message);
          setIsLoading(false);
          return;
        }

        try {
          await api.post("/v1/payment-methods", {
            paymentMethod: {
              id: paymentMethod?.id,
              brand: paymentMethod?.card?.brand,
              exp_month: paymentMethod?.card?.exp_month,
              exp_year: paymentMethod?.card?.exp_year,
              last4: paymentMethod?.card?.last4
            },
            billing_info: {
              address: data.address,
              city: data.city,
              state: data.state,
              zipcode: data.zipcode,
              country: data.country
            },
            company_id: id
          });
          await refetch();
        } catch (e) {
          const message = e?.response?.data?.message || e?.message || "";
          notify(message, {
            type: "error"
          });
          setStripeErrorMsg(message);
          setIsLoading(false);
          return;
        }
      }
    } else if (
      !!selectedDefaultCardIdState &&
      selectedDefaultCardIdState !== defaultCard?.id
    ) {
      try {
        await api.put("/v1/companies/billing-info", {
          company_id: id,
          default_payment_method_id: selectedDefaultCardIdState,
          billing_info: {
            address_display: data.address_display,
            address: data.address,
            city: data.city,
            state: data.state,
            zipcode: data.zipcode,
            country: data.country,
            address_latitude: data.address_latitude,
            address_longitude: data.address_longitude,
            google_places_id: data.google_places_id
          }
        });
        await refetch();
      } catch (e) {
        const message = e?.response?.data?.message || e?.message || "";
        notify(message, { type: "error" });
        setStripeErrorMsg(message);
        setIsLoading(false);
        return;
      }
    }

    if (data.is_verified !== companyData?.company.is_verified) {
      try {
        await api.post("/v1/companies/verify", {
          company_id: id,
          is_verified: data.is_verified,
          free_trial_days: isFreeTrialDays
        });
        await refetch();
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || "";
        notify(message, { type: "error" });
        setStripeErrorMsg(message);
        setIsLoading(false);
        return;
      }
    }

    const payload = {
      name: data.name,
      contact_email: data.contact_email || "",
      caption: data.caption || "",
      about: data.about || "",
      address_display: data.address_display,
      address: data.address,
      city: data.city,
      state: data.state,
      zipcode: data.zipcode,
      country: data.country,
      address_latitude: data.address_latitude,
      address_longitude: data.address_longitude,
      google_places_id: data.google_places_id,
      payment_type: data.payment_type,
      amenities_enums: data.amenities_enums,
      facebook_url: data.facebook_url || "",
      instagram_url: data.instagram_url || "",
      twitter_url: data.twitter_url || "",
      website_url: data?.website_url ? data.website_url : "",
      user_pays_fees: data.user_pays_fees,
      timeslot_spacing: parseInt(data.timeslot_spacing),
      late_cancellation_hours: data.late_cancellation_hours,
      late_cancellation_charge: data.late_cancellation_charge,
      no_show_charge: data.no_show_charge,
      retail_tax_rate: data.retail_tax_rate,
      amenities: [],
      default_service: data.default_service_id
        ? { id: data.default_service_id }
        : null,
      cover_photo_url: data.cover_photo_url,
      logo_url: data.logo_url
    };

    submitWithPhoto(payload);
  };

  const handleCancelPlan = async () => {
    setIsLoading(true);
    try {
      await api.post("/v1/companies/cancel-plan", {
        company_id: id
      });
      await refetch();
      setIsLoading(false);
      window.location.reload();
      return true;
    } catch (e: any) {
      const message =
        e?.response?.data?.message || e?.message || translate("common.error");
      notify(message, { type: "error" });
      setIsLoading(false);
      return false;
    }
  };

  const handleRenewPlan = async () => {
    setIsLoading(true);
    try {
      await api.post("/v1/companies/renew-plan", {
        company_id: id
      });
      await refetch();
      setIsLoading(false);
      window.location.reload();
      return true;
    } catch (e: any) {
      const message =
        e?.response?.data?.message || e?.message || translate("common.error");
      notify(message, { type: "error" });
      setIsLoading(false);
      return false;
    }
  };

  return (
    <Edit
      actions={<CustomHeaderActions title={translate("companies.company")} />}
    >
      <SimpleForm
        onSubmit={submit}
        resolver={
          permissions?.isSuperAdmin ? undefined : yupResolver(validationSchema)
        }
        noValidate
        toolbar={<CustomToolbar isLoading={isLoading} />}
      >
        <FormErrorCapture setErrorRef={formSetErrorRef} />
        <ShowAndEditDetails
          title={translate("companies.company_details")}
          view="edit"
          icon={<CompanyDetailsIcon />}
          marginBottom="13px"
          customButtons={
            <div className="flex items-center gap-3 sm:gap-2 flex-wrap w-full sm:w-auto">
              {!permissions?.isCompanyBasic && <ConnectStripeButton />}
              <Link
                to={`/dashboard/Company/${id}/show`}
                style={{ width: "inherit" }}
              >
                <CustomButton
                  text={translate("common.show")}
                  variant="secondary"
                  startIcon={<ShowButtonIcon />}
                  sx={{
                    "@media screen and (max-width:640px)": {
                      width: "100%"
                    }
                  }}
                />
              </Link>
            </div>
          }
        />
        {companyData?.company.is_verified && (
          <ChangePlan
            handleCancelPlan={handleCancelPlan}
            handleRenewPlan={handleRenewPlan}
            isLoading={isLoading}
          />
        )}
        <CustomImageInputSmall source="logo_url" />
        <CustomImageInputWrapper
          label={translate("companies.cover_image_recommended")}
        >
          <CustomImageInput
            source="cover_photo_url"
            height="250px"
            width="745px"
          />
        </CustomImageInputWrapper>
        <TextInput
          source="name"
          label={translate("companies.name")}
          fullWidth
        />
        {permissions?.isSuperAdmin && (
          <>
            <BooleanInput
              source="is_verified"
              label={translate("companies.company_verify")}
              fullWidth
              onChange={(e) => {
                if (
                  e.target.checked &&
                  !companyData?.company?.is_verified &&
                  companyData?.company?.status === "FULL"
                ) {
                  setConfirmFreeTrial(true);
                }
              }}
            />
            {confirmFreeTrial && (
              <FreeTrialModal
                open
                isFreeTrialDays={isFreeTrialDays}
                setIsFreeTrialDays={setIsFreeTrialDays}
                setConfirmFreeTrial={setConfirmFreeTrial}
              />
            )}
          </>
        )}
        <TextInput
          source="contact_email"
          label={translate("companies.contact_email")}
          fullWidth
        />
        <Labeled
          paddingBottom={2}
          label={translate("companies.payment_method")}
          fullWidth
        >
          <>
            <CardComponent
              setIsNewCardEmpty={setIsNewCardEmpty}
              setStripeErrorMsg={setStripeErrorMsg}
            />
            {stripeErrorMsg && (
              <span className="text-error text-[12px]">{stripeErrorMsg}</span>
            )}
            <ShowCards
              company={companyData?.company}
              selectedDefaultCardIdState={selectedDefaultCardIdState}
              setSelectedDefaultCardIdState={setSelectedDefaultCardIdState}
              refetch={refetch}
              editMode
            />
          </>
        </Labeled>
        <Box
          sx={{
            width: "100%",
            "& .MuiFilledInput-root": {
              borderRadius: "8px",
              border: "1px solid rgba(48, 47, 46, 0.06)",
              backgroundColor: "rgba(0, 0, 0, 0.04)",
              paddingBottom: "5px !important",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.06)"
              },
              "& fieldset": {
                border: "solid 1px transparent"
              },
              "&:hover fieldset": {
                border: "solid 1px transparent"
              },
              "&.Mui-focused fieldset": {
                border: "solid 1px #b8722a"
              }
            },
            "& .MuiFilledInput-input": {
              height: "18px",
              fontSize: "16px",
              borderRadius: "8px",
              fontFamily: "Inter",
              border: "none"
            }
          }}
        >
          <GooglePlacesAutocomplete
            label={translate("companies.billing_address")}
          />
        </Box>
        {(!permissions?.isCompanyBasic || permissions?.isSuperAdmin) && (
          <AutocompleteInput
            source="payment_type"
            label={translate("companies.payment_type")}
            choices={paymentTypes}
            translateChoice={true}
            fullWidth
            disabled={
              permissions?.isCompanyLimited && !permissions?.isSuperAdmin
            }
          />
        )}
        <TextInput
          source="caption"
          label={translate("companies.caption")}
          fullWidth
        />
        <TextInput
          source="about"
          label={translate("companies.about")}
          multiline
          fullWidth
        />
        <SelectArrayInput
          label={translate("locations.amenities")}
          fullWidth
          source="amenities_enums"
          choices={amenitiesChoices}
          format={(v) => {
            return v?.map(
              (item: string) =>
                amenitiesChoices?.find((curr) => curr.id === item)?.id
            );
          }}
        />
        <TextInput
          source="facebook_url"
          multiline
          fullWidth
          label="Facebook"
          helperText="https://facebook.com/pointment"
        />
        <TextInput
          source="instagram_url"
          multiline
          fullWidth
          label="Instagram"
          helperText="@pointment"
        />
        <TextInput
          source="twitter_url"
          multiline
          fullWidth
          label="Twitter"
          helperText="@pointment"
        />
        <TextInput
          source="website_url"
          multiline
          fullWidth
          label="Website"
          helperText="https://pointmentapp.com"
        />
        <AdvancedDetailsContainer translate={translate} />
      </SimpleForm>
    </Edit>
  );
}

const validationSchema = yup
  .object()
  .shape({
    name: yup.string().required("Company name required."),
    contact_email: yup.string().email("Invalid email.").nullable(),
    caption: yup.string().nullable(),
    about: yup.string().nullable(),
    cover_photo_url: yup.object().nullable(),
    logo_url: yup.object().nullable(),
    facebook_url: yup
      .string()
      .matches(facebookRegex, { excludeEmptyString: true })
      .nullable(),
    instagram_url: yup
      .string()
      .matches(atUsernameRegex, { excludeEmptyString: true })
      .nullable(),
    twitter_url: yup
      .string()
      .matches(atUsernameRegex, { excludeEmptyString: true })
      .nullable(),
    website_url: yup.string().url("This field must be a valid URL").nullable(),
    default_service_id: yup.string().nullable(),
    payment_type: yup.string().required("Payment type required."),
    status: yup.string().required("Status required."),
    timeslot_spacing: yup.string().required("Timeslot spacing required."),
    late_cancellation_hours: yup
      .number()
      .required("Late cancellation hours required."),
    late_cancellation_charge: yup
      .number()
      .min(0, "Value must be more than or equal to 0%")
      .max(1, "Value must be less than or equal to 100%")
      .required("Late cancellation charge required."),
    no_show_charge: yup
      .number()
      .min(0, "Value must be more than or equal to 0%")
      .max(1, "Value must be less than or equal to 100%")
      .required("No show charge required."),
    retail_tax_rate: yup
      .number()
      .min(0, "Value must be more than or equal to 0%")
      .max(1, "Value must be less than or equal to 100%"),
    address_display: yup.string().nullable(),
    address_latitude: yup.number().nullable(),
    address_longitude: yup.number().nullable(),
    address: yup
      .string()
      .nullable()
      .when("status", {
        is: (val: string) => val !== "BASIC",
        then: (schema) => schema.required("Address is required"),
        otherwise: (schema) => schema.notRequired()
      }),
    city: yup
      .string()
      .nullable()
      .when("status", {
        is: (val: string) => val !== "BASIC",
        then: (schema) => schema.required("City is required"),
        otherwise: (schema) => schema.notRequired()
      }),
    state: yup
      .string()
      .nullable()
      .when("status", {
        is: (val: string) => val !== "BASIC",
        then: (schema) => schema.required("State is required"),
        otherwise: (schema) => schema.notRequired()
      }),
    zipcode: yup
      .string()
      .nullable()
      .when("status", {
        is: (val: string) => val !== "BASIC",
        then: (schema) => schema.required("Zipcode is required"),
        otherwise: (schema) => schema.notRequired()
      }),
    country: yup
      .string()
      .nullable()
      .when("status", {
        is: (val: string) => val !== "BASIC",
        then: (schema) => schema.required("Country is required"),
        otherwise: (schema) => schema.notRequired()
      })
  })
  .required();
